import React, { useState, useEffect, useMemo } from 'react'
import {
  Sparkles,
  Plus,
  Trash2,
  Edit2,
  X,
  Save,
  RefreshCw,
  Tag,
  AlertTriangle,
  Package,
  Layers,
  Percent,
  ArrowLeft,
  Check,
} from 'lucide-react'
import { apiGet, apiPost, apiPut, apiDelete, apiGetWithMeta } from '../lib/api'
import { AppDataTable } from './AppDataTable'
import { SearchableSelect } from './SearchableSelect'
import { ProductImage } from './ProductImage'
import type { ComboOffer, Product, ProductVariant } from '@buddys-choice/shared'

interface ComboItemFormValue {
  productId: string
  variantId: string | null
  quantity: number
}

export const AdminCombos: React.FC = () => {
  const [combos, setCombos] = useState<ComboOffer[]>([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'list' | 'create' | 'edit'>('list')
  const [editingCombo, setEditingCombo] = useState<ComboOffer | null>(null)
  const [products, setProducts] = useState<Product[]>([])

  // Form State
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [sku, setSku] = useState('')
  const [price, setPrice] = useState<number>(0)
  const [stockQty, setStockQty] = useState<number>(10)
  const [lowStockThreshold, setLowStockThreshold] = useState<number>(5)
  const [isActive, setIsActive] = useState(true)
  const [comboItems, setComboItems] = useState<ComboItemFormValue[]>([
    { productId: '', variantId: null, quantity: 1 },
    { productId: '', variantId: null, quantity: 1 },
  ])
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  // Map of loaded product details for variant selections
  const [productDetailsMap, setProductDetailsMap] = useState<Record<string, Product>>({})

  useEffect(() => {
    fetchCombos()
    fetchProducts()
  }, [])

  const fetchCombos = () => {
    setLoading(true)
    apiGet<ComboOffer[]>('combos')
      .then((data) => setCombos(data || []))
      .catch((err) => console.error('Fetch combos error:', err))
      .finally(() => setLoading(false))
  }

  const fetchProducts = async () => {
    try {
      const first = await apiGetWithMeta<Product[]>('products', { page: 1, pageSize: 100 })
      let allProds = first.data || []
      const totalPages = first.meta?.totalPages ?? 1
      for (let p = 2; p <= totalPages; p++) {
        const next = await apiGetWithMeta<Product[]>('products', { page: p, pageSize: 100 })
        if (next.data) allProds = [...allProds, ...next.data]
      }
      setProducts(allProds)
    } catch (err) {
      console.error('Fetch products error:', err)
    }
  }

  // Load product details (including variants) when a product is selected in item builder
  const ensureProductDetails = async (productId: string) => {
    if (!productId || productDetailsMap[productId]) return
    try {
      const detailed = await apiGet<Product>(`products/${productId}`)
      setProductDetailsMap((prev) => ({ ...prev, [productId]: detailed }))
    } catch (err) {
      console.error(`Failed to load details for product ${productId}:`, err)
    }
  }

  const productOptions = useMemo(
    () =>
      products.map((p) => ({
        value: p.id,
        label: `${p.name} — ₹${p.basePrice?.toLocaleString('en-IN') ?? 0} (${p.sku})`,
        keywords: `${p.name} ${p.sku} ${p.category}`,
      })),
    [products],
  )

  // Real-time calculation of original regular price for mapped items
  const originalTotalPrice = useMemo(() => {
    return comboItems.reduce((acc, item) => {
      if (!item.productId) return acc
      const prod = productDetailsMap[item.productId] || products.find((p) => p.id === item.productId)
      if (!prod) return acc
      let itemPrice = prod.basePrice ?? 0
      if (item.variantId && prod.variants) {
        const variant = prod.variants.find((v) => v.id === item.variantId)
        if (variant && variant.priceOverride != null) {
          itemPrice = variant.priceOverride
        }
      }
      return acc + itemPrice * (item.quantity || 1)
    }, 0)
  }, [comboItems, productDetailsMap, products])

  const discountAmount = useMemo(() => {
    if (originalTotalPrice <= 0 || price <= 0) return 0
    return Math.max(0, originalTotalPrice - price)
  }, [originalTotalPrice, price])

  const discountPercentage = useMemo(() => {
    if (originalTotalPrice <= 0 || discountAmount <= 0) return 0
    return Math.round((discountAmount / originalTotalPrice) * 100)
  }, [originalTotalPrice, discountAmount])

  const handleOpenCreateModal = async () => {
    setEditingCombo(null)
    setName('')
    setDescription('')
    setPrice(0)
    setStockQty(10)
    setLowStockThreshold(5)
    setIsActive(true)
    setFormError('')
    setComboItems([
      { productId: '', variantId: null, quantity: 1 },
      { productId: '', variantId: null, quantity: 1 },
    ])
    setViewMode('create')
    try {
      const res = await apiGet<{ sku: string }>('combos/next-sku')
      setSku(res.sku)
    } catch {
      setSku('')
    }
  }

  const handleOpenEditModal = async (combo: ComboOffer) => {
    setEditingCombo(combo)
    setName(combo.name)
    setDescription(combo.description || '')
    setSku(combo.sku)
    setPrice(combo.price)
    setStockQty(combo.stockQty)
    setLowStockThreshold(combo.lowStockThreshold)
    setIsActive(combo.isActive)
    setFormError('')

    const mappedItems: ComboItemFormValue[] = combo.items.map((i) => ({
      productId: i.productId,
      variantId: i.variantId ?? null,
      quantity: i.quantity,
    }))
    setComboItems(mappedItems.length >= 2 ? mappedItems : [
      { productId: '', variantId: null, quantity: 1 },
      { productId: '', variantId: null, quantity: 1 },
    ])

    setViewMode('edit')
    // Pre-fetch product details for all items in combo
    combo.items.forEach((item) => ensureProductDetails(item.productId))
  }

  const handleItemProductChange = (index: number, pId: string) => {
    setComboItems((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index]!, productId: pId, variantId: null }
      return updated
    })
    ensureProductDetails(pId)
  }

  const handleItemVariantChange = (index: number, vId: string | null) => {
    setComboItems((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index]!, variantId: vId }
      return updated
    })
  }

  const handleItemQuantityChange = (index: number, qty: number) => {
    setComboItems((prev) => {
      const updated = [...prev]
      updated[index] = { ...updated[index]!, quantity: Math.max(1, qty) }
      return updated
    })
  }

  const handleAddItemRow = () => {
    setComboItems((prev) => [...prev, { productId: '', variantId: null, quantity: 1 }])
  }

  const handleRemoveItemRow = (index: number) => {
    if (comboItems.length <= 2) {
      setFormError('A combo offer requires at least 2 items.')
      return
    }
    setComboItems((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmitCombo = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    if (!name.trim()) return setFormError('Combo name is required.')
    if (price < 0) return setFormError('Price cannot be negative.')

    const validItems = comboItems.filter((i) => i.productId.trim() !== '')
    if (validItems.length < 2) return setFormError('Please select at least 2 products/variants for this combo offer.')

    setSaving(true)
    const payload = {
      name: name.trim(),
      description: description.trim() || undefined,
      sku: sku.trim() || undefined,
      price: Number(price),
      stockQty: Number(stockQty),
      lowStockThreshold: Number(lowStockThreshold),
      isActive,
      items: validItems.map((i) => ({
        productId: i.productId,
        variantId: i.variantId || undefined,
        quantity: i.quantity,
      })),
    }

    try {
      if (editingCombo) {
        await apiPut(`combos/${editingCombo.id}`, payload)
      } else {
        await apiPost('combos', payload)
      }
      setViewMode('list')
      fetchCombos()
    } catch (err: unknown) {
      setFormError((err as Error).message || 'Failed to save combo offer.')
    } finally {
      setSaving(false)
    }
  }

  const handleArchiveCombo = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to archive combo '${name}'?`)) return
    try {
      await apiDelete(`combos/${id}`)
      fetchCombos()
    } catch (err: unknown) {
      alert((err as Error).message || 'Failed to archive combo.')
    }
  }

  const columns = useMemo(
    () => [
      {
        name: 'SKU',
        selector: (c: ComboOffer) => (
          <span className="font-mono text-[var(--gold-main)] font-semibold">{c.sku}</span>
        ),
      },
      {
        name: 'Combo Name',
        cell: (c: ComboOffer) => (
          <div>
            <p className="font-medium text-[var(--text-primary)]">{c.name}</p>
            {c.description && <p className="text-[10px] text-[var(--text-muted)] line-clamp-1">{c.description}</p>}
          </div>
        ),
      },
      {
        name: 'Included Items',
        cell: (c: ComboOffer) => (
          <div className="space-y-1.5 py-1">
            {c.items.map((item, idx) => {
              const matchedProd = products.find((p) => p.id === item.productId)
              return (
                <div key={idx} className="text-xs flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md overflow-hidden bg-[var(--bg-surface-2)] border border-[var(--border)] shrink-0">
                    <ProductImage src={matchedProd?.heroImageUrl ?? null} alt={item.productName || 'Item'} className="w-full h-full" imgClassName="w-full h-full object-cover" />
                  </div>
                  <span className="font-mono text-[var(--gold-main)] font-bold">{item.quantity}x</span>
                  <span className="font-medium">{item.productName || 'Product'}</span>
                  {item.variantDescription && (
                    <span className="px-1.5 py-0.5 rounded bg-[var(--bg-surface-2)] text-[10px] text-[var(--gold-dark)] border border-[var(--border)] font-mono">
                      {item.variantDescription}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        ),
      },
      {
        name: 'Pricing & Discount',
        cell: (c: ComboOffer) => {
          const originalPrice = c.items.reduce((acc, i) => acc + (i.basePrice ?? 0) * (i.quantity ?? 1), 0)
          const discountAmount = Math.max(0, originalPrice - c.price)
          const discountPercentage = originalPrice > 0 && discountAmount > 0 ? Math.round((discountAmount / originalPrice) * 100) : 0

          return (
            <div className="flex flex-col gap-0.5 py-1">
              <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-[var(--gold-main)] text-sm">₹{c.price.toLocaleString('en-IN')}</span>
                {discountPercentage > 0 && (
                  <span className="bg-emerald-500/15 text-emerald-400 font-mono text-[10px] px-1.5 py-0.5 rounded border border-emerald-500/30 font-extrabold">
                    {discountPercentage}% OFF
                  </span>
                )}
              </div>
              {originalPrice > 0 && (
                <span className="text-[11px] font-mono text-[var(--text-muted)] line-through decoration-red-400 decoration-1">
                  Regular: ₹{originalPrice.toLocaleString('en-IN')}
                </span>
              )}
            </div>
          )
        },
      },
      {
        name: 'Stock',
        selector: (c: ComboOffer) => (
          <span className={`font-mono font-bold ${c.stockQty <= c.lowStockThreshold ? 'text-amber-500' : ''}`}>
            {c.stockQty}
          </span>
        ),
      },
      {
        name: 'Status',
        cell: (c: ComboOffer) => (
          <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${c.isActive ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/25' : 'bg-zinc-500/10 text-zinc-400 border border-zinc-500/25'}`}>
            {c.isActive ? 'Active' : 'Disabled'}
          </span>
        ),
      },
      {
        name: 'Actions',
        right: true,
        cell: (c: ComboOffer) => (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => handleOpenEditModal(c)}
              className="admin-action-chip admin-action-chip--secondary"
            >
              <Edit2 className="w-3.5 h-3.5" /> Edit
            </button>
            <button
              type="button"
              onClick={() => handleArchiveCombo(c.id, c.name)}
              className="admin-action-chip admin-action-chip--danger"
            >
              <Trash2 className="w-3.5 h-3.5" /> Archive
            </button>
          </div>
        ),
      },
    ],
    [products],
  )

  if (viewMode === 'list') {
    return (
      <div className="space-y-6 animate-fade-in-up">
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-title font-bold text-[var(--text-primary)] flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[var(--gold-main)] shrink-0" />
              Combo Offers & Bundles
            </h2>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              Create special multi-product & variant bundle offers at discounted prices.
            </p>
          </div>
          <button
            type="button"
            onClick={handleOpenCreateModal}
            className="btn btn-primary flex items-center gap-2 text-xs font-semibold self-start sm:self-auto"
          >
            <Plus className="w-4 h-4" /> Create Combo Offer
          </button>
        </div>

        {/* Combos Table */}
        <div className="glass-luxury-card overflow-hidden">
          <AppDataTable
            columns={columns}
            data={combos}
            keyField={(c) => c.id}
            progressPending={loading}
            noDataComponent="No combo offers created yet."
          />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in-up max-w-5xl mx-auto">
      {/* Top Header & Back Navigation */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[var(--border)] pb-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setViewMode('list')}
            className="btn-gold-outline text-xs py-2 px-3.5 rounded-xl flex items-center gap-1.5 shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Offers</span>
          </button>
          <div>
            <h2 className="text-xl sm:text-2xl font-title font-bold text-[var(--text-primary)] flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[var(--gold-main)] shrink-0" />
              {viewMode === 'edit' ? `Edit Combo Offer: ${sku}` : 'Create New Combo Offer'}
            </h2>
            <p className="text-xs text-[var(--text-muted)] mt-0.5">
              Map multiple products & variants to create discounted bundles.
            </p>
          </div>
        </div>
      </div>

      {/* Main Full-Page Form Card */}
      <form onSubmit={handleSubmitCombo} className="glass-luxury-card p-6 sm:p-8 space-y-6">
        {formError && (
          <div className="alert alert-error text-xs flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>{formError}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="form-group">
            <label className="form-label">Combo Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Royal Wedding Bridal Set"
              className="form-input text-xs"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Combo SKU <span className="text-[var(--text-faint)] font-normal">(Auto-generated)</span>
            </label>
            <input
              type="text"
              value={sku}
              readOnly
              disabled
              placeholder="AUTO-GENERATED"
              className="form-input font-mono uppercase bg-[var(--bg-surface-2)] text-[var(--text-muted)] cursor-not-allowed font-bold"
            />
            <p className="text-[10px] text-[var(--text-faint)] mt-1">
              Auto-generated unique combo SKU. Cannot be edited.
            </p>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Description (Optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="e.g. Includes handcrafted necklace and matching drop earrings."
            className="form-input text-xs resize-none"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div className="form-group">
            <label className="form-label">Combo Offer Price (₹) *</label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(Number(e.target.value))}
              min={0}
              className="form-input font-mono font-bold text-[var(--gold-main)] text-sm"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Combo Stock Qty *</label>
            <input
              type="number"
              value={stockQty}
              onChange={(e) => setStockQty(Number(e.target.value))}
              min={0}
              className="form-input font-mono text-xs"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Low Stock Threshold</label>
            <input
              type="number"
              value={lowStockThreshold}
              onChange={(e) => setLowStockThreshold(Number(e.target.value))}
              min={0}
              className="form-input font-mono text-xs"
            />
          </div>
        </div>

        {/* Real-time Pricing Summary Widget */}
        {originalTotalPrice > 0 && (
          <div className="p-4 rounded-xl border border-[var(--gold-main)]/30 bg-gradient-to-r from-[var(--gold-main)]/10 via-[var(--bg-surface-2)] to-[var(--bg-main)] flex flex-wrap items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-2.5">
              <Tag className="w-4 h-4 text-[var(--gold-main)] shrink-0" />
              <div>
                <span className="text-[var(--text-muted)] font-medium">Regular Items Price: </span>
                <span className="font-mono font-bold text-[var(--text-primary)] line-through">₹{originalTotalPrice.toLocaleString('en-IN')}</span>
              </div>
            </div>

            {discountAmount > 0 ? (
              <div className="flex items-center gap-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 px-3.5 py-1.5 rounded-lg font-bold">
                <Percent className="w-4 h-4 shrink-0" />
                <span>Customer Savings: ₹{discountAmount.toLocaleString('en-IN')} ({discountPercentage}% OFF)</span>
              </div>
            ) : (
              <div className="text-[11px] text-[var(--text-muted)] italic">
                Set Combo Offer Price below ₹{originalTotalPrice.toLocaleString('en-IN')} to offer a bundle discount!
              </div>
            )}
          </div>
        )}

        {/* Variant-Level Combo Item Selection Builder */}
        <div className="space-y-4 pt-3 border-t border-[var(--border)]">
          <div className="flex items-center justify-between">
            <div>
              <label className="form-label font-bold text-[var(--text-primary)] text-sm">
                Map Products & Variants to Combo (Minimum 2) *
              </label>
              <p className="text-[11px] text-[var(--text-muted)]">Select at least 2 items to bundle together for this offer.</p>
            </div>
            <button
              type="button"
              onClick={handleAddItemRow}
              className="btn-gold-outline text-xs py-2 px-3.5 flex items-center gap-1.5 rounded-xl font-semibold"
            >
              <Plus className="w-3.5 h-3.5" /> Add Item Row
            </button>
          </div>

          <div className="space-y-3">
            {comboItems.map((item, idx) => {
              const selectedProduct = productDetailsMap[item.productId] || products.find((p) => p.id === item.productId)
              const availableVariants: ProductVariant[] = selectedProduct?.variants || []

              return (
                <div key={idx} className="glass-card p-3.5 sm:p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 border border-[var(--border)]">
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[11px] font-mono text-[var(--gold-main)] font-bold shrink-0">
                      #{idx + 1}
                    </span>
                    <div className="w-10 h-10 rounded-lg overflow-hidden bg-[var(--bg-surface-2)] border border-[var(--border)] shrink-0 flex items-center justify-center">
                      <ProductImage src={selectedProduct?.heroImageUrl ?? null} alt={selectedProduct?.name || 'Item'} className="w-full h-full" imgClassName="w-full h-full object-cover" />
                    </div>
                  </div>

                  {/* Product Selector */}
                  <div className="flex-1 min-w-0">
                    <SearchableSelect
                      value={item.productId}
                      onChange={(val) => handleItemProductChange(idx, val)}
                      options={productOptions}
                      placeholder="Select product..."
                      aria-label={`Combo item ${idx + 1} product`}
                    />
                  </div>

                  {/* Variant Selector */}
                  {item.productId && (
                    <div className="w-full sm:w-56 shrink-0">
                      {availableVariants.length > 0 ? (
                        <select
                          value={item.variantId || ''}
                          onChange={(e) => handleItemVariantChange(idx, e.target.value || null)}
                          className="form-input text-xs py-2.5"
                        >
                          <option value="">— All / Standard Variant —</option>
                          {availableVariants.map((v) => {
                            const attrs = v.attributes?.map((a) => `${a.attrTypeId}: ${a.attrValue}`).join(', ') || v.sku
                            return (
                              <option key={v.id} value={v.id}>
                                {attrs} (₹{v.priceOverride ?? selectedProduct?.basePrice})
                              </option>
                            )
                          })}
                        </select>
                      ) : (
                        <span className="text-[10px] text-[var(--text-muted)] italic px-2 block truncate">Standard product</span>
                      )}
                    </div>
                  )}

                  {/* Quantity */}
                  <div className="w-full sm:w-24 shrink-0 flex items-center gap-1.5">
                    <span className="text-xs text-[var(--text-muted)] sm:hidden font-medium">Qty:</span>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemQuantityChange(idx, Number(e.target.value))}
                      min={1}
                      max={99}
                      className="form-input text-xs font-mono text-center py-2.5"
                      title="Quantity in bundle"
                    />
                  </div>

                  {/* Delete row */}
                  <button
                    type="button"
                    onClick={() => handleRemoveItemRow(idx)}
                    className="p-2 text-[var(--text-muted)] hover:text-red-400 rounded-xl hover:bg-red-500/10 shrink-0 self-center transition"
                    title="Remove item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )
            })}
          </div>
        </div>

        {/* Status Toggle */}
        <div className="flex items-center gap-2.5 pt-2">
          <input
            type="checkbox"
            id="comboIsActive"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="w-4 h-4 rounded border-[var(--border)] text-[var(--gold-main)] focus:ring-[var(--gold-main)] cursor-pointer"
          />
          <label htmlFor="comboIsActive" className="text-xs font-medium cursor-pointer text-[var(--text-primary)]">
            Enable and publish this combo offer on storefront
          </label>
        </div>

        {/* Form Buttons */}
        <div className="flex items-center justify-end gap-3 pt-5 border-t border-[var(--border)]">
          <button
            type="button"
            onClick={() => setViewMode('list')}
            className="btn-gold-outline text-xs py-2.5 px-6 font-medium rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-gold text-xs py-2.5 px-7 font-bold flex items-center gap-2 rounded-xl"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span>{viewMode === 'edit' ? 'Save Changes' : 'Create Combo Offer'}</span>
          </button>
        </div>
      </form>
    </div>
  )
}

export default AdminCombos
